import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Factory, Footprints, ShoppingBag, Wrench, Hammer, Banknote, GraduationCap, Globe2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const PARTNER_TYPES = [
  { value: "leather-manufacturer", label: "Leather Manufacturer", icon: Factory, body: "Wet-blue, crust, finished leather producers seeking African production capacity." },
  { value: "footwear", label: "Footwear Brand / Producer", icon: Footprints, body: "Italian and European footwear brands placing production lines." },
  { value: "handbag", label: "Handbag / Accessories House", icon: ShoppingBag, body: "Luxury handbag and small leather goods houses." },
  { value: "machinery-supplier", label: "Machinery & Equipment Supplier", icon: Wrench, body: "Tannery drums, finishing presses, cutting & stitching technology." },
  { value: "construction", label: "Construction Partner", icon: Hammer, body: "Industrial construction firms for tannery and manufacturing facilities." },
  { value: "financier", label: "Financier / Capital Partner", icon: Banknote, body: "Equity, debt, project finance, DFI co-investment." },
  { value: "training-institution", label: "Training Institution", icon: GraduationCap, body: "Vocational and technical training partners for skills transfer programmes." },
  { value: "export-buyer", label: "Export Buyer / Distributor", icon: Globe2, body: "EU, US and global luxury distributors for offtake agreements." },
];

export default function Partner() {
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    country: "",
    partner_type: "",
    capabilities: "",
    annual_volume: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/partners", form);
      setSubmitted(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const pickType = (v) => {
    setSelected(v);
    setForm((f) => ({ ...f, partner_type: v }));
  };

  return (
    <div data-testid="page-partner" className="bg-[#F9F9F6]">
      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-16 max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-9">
            <p className="eyebrow">Partner Onboarding</p>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight mt-6">
              Manufacture in <span className="italic text-[#5C2018]">Botswana.</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-[#1A1A1A] max-w-2xl leading-relaxed">
              The Botswana Leather Project is built as an open execution platform. Eight partner tracks. From machinery suppliers to luxury houses. Come together inside one SEZA-aligned operation.
            </p>
          </div>
        </div>
      </section>

      {/* Partner type grid */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 pb-16" data-testid="partner-type-grid">
        <p className="eyebrow mb-6">Step 01. Select track</p>
        <div className="grid grid-cols-2 md:grid-cols-4 border-l border-t border-[#1A1A1A]">
          {PARTNER_TYPES.map((p) => {
            const Icon = p.icon;
            const active = selected === p.value;
            return (
              <button
                type="button"
                key={p.value}
                onClick={() => pickType(p.value)}
                data-testid={`partner-type-${p.value}`}
                className={`text-left border-r border-b border-[#1A1A1A] p-6 md:p-7 transition-colors duration-300 ${
                  active ? "bg-[#5C2018] text-[#F9F9F6]" : "bg-[#F9F9F6] hover:bg-white"
                }`}
              >
                <div className={`w-10 h-10 border flex items-center justify-center ${active ? "border-[#F9F9F6]" : "border-[#1A1A1A]"}`}>
                  <Icon size={18} />
                </div>
                <h3 className={`font-serif text-lg md:text-xl tracking-tight mt-5 leading-tight ${active ? "text-[#F9F9F6]" : "text-[#1A1A1A]"}`}>
                  {p.label}
                </h3>
                <p className={`text-xs mt-2 leading-relaxed ${active ? "text-[#E0D8CD]" : "text-[#666666]"}`}>{p.body}</p>
                {active && <p className="mt-4 text-[10px] uppercase tracking-[0.24em] text-[#E5C4A1] inline-flex items-center gap-1"><Check size={12} /> selected</p>}
              </button>
            );
          })}
        </div>
      </section>

      {/* Form */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 pb-24" data-testid="partner-form-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <p className="eyebrow">Step 02. Application</p>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-4 leading-tight">
              Tell us how you operate.
            </h2>
            <p className="mt-5 text-sm text-[#666666] leading-relaxed">
              We review every application within five business days. Material partners receive a direct briefing from LRG Holdings.
            </p>
            <p className="mt-6 text-xs text-[#666666]">Submissions are routed to <span className="font-medium text-[#1A1A1A]">lefikaragontse@lrgholdings.com</span>.</p>
          </div>

          <div className="md:col-span-8">
            {submitted ? (
              <div data-testid="partner-success" className="border border-[#5C2018] p-8 md:p-10 bg-white">
                <p className="eyebrow text-[#5C2018]">Received</p>
                <h3 className="font-serif text-3xl mt-4 tracking-tight">Your application is in the LRG pipeline.</h3>
                <p className="mt-4 text-sm text-[#666666] leading-relaxed">
                  Our team will review and respond directly to <span className="font-medium text-[#1A1A1A]">{form.email}</span>.
                </p>
                <Link to="/" data-testid="partner-back-home" className="mt-8 inline-flex items-center gap-3 px-7 py-4 bg-[#5C2018] text-[#F9F9F6] text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-[#1A1A1A]">
                  Return to site <ArrowRight size={14} />
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5" data-testid="partner-form">
                <SmallField label="Company name" required>
                  <input required type="text" value={form.company_name} onChange={change("company_name")} data-testid="partner-company" className="mt-2 w-full bg-transparent border-b border-[#1A1A1A] py-3 focus:outline-none focus:border-[#5C2018]" />
                </SmallField>
                <SmallField label="Contact name" required>
                  <input required type="text" value={form.contact_name} onChange={change("contact_name")} data-testid="partner-contact" className="mt-2 w-full bg-transparent border-b border-[#1A1A1A] py-3 focus:outline-none focus:border-[#5C2018]" />
                </SmallField>
                <SmallField label="Email" required>
                  <input required type="email" value={form.email} onChange={change("email")} data-testid="partner-email" className="mt-2 w-full bg-transparent border-b border-[#1A1A1A] py-3 focus:outline-none focus:border-[#5C2018]" />
                </SmallField>
                <SmallField label="Phone">
                  <input type="text" value={form.phone} onChange={change("phone")} data-testid="partner-phone" className="mt-2 w-full bg-transparent border-b border-[#1A1A1A] py-3 focus:outline-none focus:border-[#5C2018]" />
                </SmallField>
                <SmallField label="Country">
                  <input type="text" value={form.country} onChange={change("country")} data-testid="partner-country" className="mt-2 w-full bg-transparent border-b border-[#1A1A1A] py-3 focus:outline-none focus:border-[#5C2018]" />
                </SmallField>
                <SmallField label="Partner type" required>
                  <select required value={form.partner_type} onChange={change("partner_type")} data-testid="partner-type-select" className="mt-2 w-full bg-transparent border-b border-[#1A1A1A] py-3 focus:outline-none focus:border-[#5C2018]">
                    <option value="">Select…</option>
                    {PARTNER_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </SmallField>
                <SmallField label="Capabilities / specialty" className="md:col-span-2">
                  <textarea rows={3} value={form.capabilities} onChange={change("capabilities")} data-testid="partner-capabilities" className="mt-2 w-full border border-[#E5E5E0] p-3 focus:outline-none focus:border-[#5C2018]" placeholder="What you do best, certifications, key clients…" />
                </SmallField>
                <SmallField label="Annual volume / scale">
                  <input type="text" value={form.annual_volume} onChange={change("annual_volume")} data-testid="partner-volume" className="mt-2 w-full bg-transparent border-b border-[#1A1A1A] py-3 focus:outline-none focus:border-[#5C2018]" placeholder="e.g. 2M units, $50M revenue" />
                </SmallField>
                <SmallField label="Message" className="md:col-span-2">
                  <textarea rows={4} value={form.message} onChange={change("message")} data-testid="partner-message" className="mt-2 w-full border border-[#E5E5E0] p-3 focus:outline-none focus:border-[#5C2018]" placeholder="What you propose to bring to the project…" />
                </SmallField>

                {error && (
                  <p data-testid="partner-error" className="md:col-span-2 text-sm text-[#5C2018] border-l-2 border-[#5C2018] pl-3">{error}</p>
                )}

                <button type="submit" disabled={submitting} data-testid="partner-submit" className="md:col-span-2 inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#5C2018] text-[#F9F9F6] text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-[#1A1A1A] transition-colors disabled:opacity-60">
                  {submitting ? "Submitting…" : "Submit application"}
                  <ArrowRight size={16} />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function SmallField({ label, className = "", required, children }) {
  return (
    <div className={className}>
      <label className="eyebrow">
        {label}
        {required && <span className="text-[#5C2018] ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}
